import React, { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ReactPaginate from 'react-paginate'
import styles from './Pagination.module.scss'
import { Text } from 'components/Text'
import { Select } from 'components/Select'
import { Icon } from 'components/Icon'
import { buildSimpleOptions, buildSimpleOption } from 'utils/selectOption'
import { Flex } from 'components/Flex'

type PaginationProps = {
  totalItems: number
  offset: number
  limit: number
  setFilters: (filter: { offset?: number; limit?: number }) => void
}

const ITEMS_PER_PAGE_OPTIONS = buildSimpleOptions([10, 15, 20, 25, 30])

export const Pagination: FC<PaginationProps> = ({ totalItems, offset, limit, setFilters }) => {
  const { t } = useTranslation()
  const [currentPage, setCurrentPage] = useState(offset / limit)

  const handlePageChange = (page: number) => {
    setFilters({ offset: page * limit })
  }

  useEffect(() => {
    setCurrentPage(offset / limit)
  }, [limit, offset])

  const pagesNumber = Math.ceil(totalItems / limit)

  const showing = limit + offset > totalItems ? totalItems : limit + offset

  const pages = Array.from(Array(pagesNumber), (_, index) => index + 1)

  return (
    <Flex justify="between" align="center" className={styles.pagination}>
      <Flex align="center" className={styles.leftSide}>
        <div className={styles.showing}>
          <Text weight="strong" className={styles.textLight}>
            {t('project.table.pagination.showing')}
            <span className={styles.textDark}> {offset + ' - ' + showing} </span>
            {t('project.table.pagination.outOf')}
            <span className={styles.textDark}> {totalItems} </span>
            {t('project.table.pagination.projects')}
          </Text>
        </div>

        <div className={styles.limit}>
          <Text weight="strong" className={styles.textLight}>
            {t('project.table.pagination.resultsPerPage')}
          </Text>

          <div className={styles.limitSelect}>
            <Select
              value={buildSimpleOption(limit)}
              onChange={(option) => setFilters({ limit: Number(option?.value), offset: 0 })}
              options={ITEMS_PER_PAGE_OPTIONS}
            />
          </div>
        </div>
      </Flex>

      <Flex align="center" className={styles.rightSide}>
        <ReactPaginate
          pageRangeDisplayed={1}
          marginPagesDisplayed={2}
          pageCount={pagesNumber}
          previousLabel={
            <Icon
              name="arrow-drop-left-line"
              size="xl"
              label={t('project.table.pagination.previousPage')}
            />
          }
          nextLabel={
            <Icon
              name="arrow-drop-right-line"
              size="xl"
              label={t('project.table.pagination.nextPage')}
            />
          }
          breakLabel="…"
          onPageChange={(data) => handlePageChange(data.selected)}
          nextClassName="next"
          previousClassName="prev"
          forcePage={currentPage}
        />

        <div className={styles.jumpTo}>
          <Text weight="strong" className={styles.textLight}>
            {t('project.table.pagination.jump')}
          </Text>

          <div className={styles.jumpToSelect}>
            <Select
              value={buildSimpleOption(currentPage + 1)}
              onChange={(option) => handlePageChange(Number(option?.value) - 1)}
              options={buildSimpleOptions(pages)}
            />
          </div>
        </div>
      </Flex>
    </Flex>
  )
}
